import { EditorMountEvent } from '@progress/kendo-react-editor';
import { produce } from 'immer';
import _ from 'lodash';
import { LanguageTypes } from '../../../../../common/enums';
import { getRequiredValidationError } from '../../../../../common/global/helpers';
import { ErrorState, ValidationError } from '../../../../benefit/models/Errors';
import {
  emailRegex,
  localStorageKeys,
  onlyDigitsRegex,
} from '../../../constants';
import {
  AddProductSteps,
  CodeSourceTypes,
  FilterTypes,
  PercentageVoucherCodeTypes,
  ProductActionTypes,
  ProductLocationTypes,
  ProductTypes,
  SoCodeTypes,
} from '../../../enums';
import { CodeModel } from '../../../models/codes';
import {
  AddCodesCommand,
  AddProductCommand,
  GiftCardOfferDetails,
  PercentageVoucherOfferDetails,
  ProductOrServiceOfferDetails,
  ValueVoucherOfferDetails,
} from '../../../models/products';
import { resources } from '../../../resources/resources';
import { GiftCardOfferDetailsForm } from './OfferDetails/GiftCard';
import { PercentageVoucherOfferDetailsForm } from './OfferDetails/PercentageVoucher';
import { ProductOrServiceOfferDetailsForm } from './OfferDetails/ProductOrService';
import { ValueVoucherOfferDetailsForm } from './OfferDetails/ValueVoucher';

import {
  addressField,
  brandDescriptionField,
  descriptionField,
  descriptionInitialValue,
  emailField,
  howToOrderField,
  nameField,
  phoneNumberField,
  ProductContext,
  websiteField,
} from './constants';
import { UploadFileInfo } from '@progress/kendo-react-upload/dist/npm/interfaces/UploadFileInfo';
import { CountyFilterModel } from '../../../models/filters';
import { NamedField } from '../../../models/common';

export const getLocationFilterIds = (
  detailsForm: ProductDetailsForm,
): number[] => {
  let filterIds: number[] = [];
  if (detailsForm.locationType) {
    if (detailsForm.locationType === ProductLocationTypes.Online) {
      filterIds.push(FilterTypes.OnlineStore);
    }
    if (detailsForm.locationType === ProductLocationTypes.SpecificLocations) {
      filterIds = [
        ...(detailsForm.selectedCountiesWithCities?.map(e => e.filterId) ?? []),
        ...(detailsForm.selectedCountiesWithCities
          ?.flatMap(e => e.cities)
          .map(e => e.filterId) ?? []),
        FilterTypes.PhysicalStore,
      ];
    }
    if (
      detailsForm.locationType ===
      ProductLocationTypes.OnlineAndSpecificLocations
    ) {
      filterIds = [
        FilterTypes.OnlineStore,
        ...(detailsForm.selectedCountiesWithCities?.map(e => e.filterId) ?? []),
        ...(detailsForm.selectedCountiesWithCities
          ?.flatMap(e => e.cities)
          .map(e => e.filterId) ?? []),
        FilterTypes.PhysicalStore,
      ];
    }
  }
  return filterIds;
};

const getCodeSourceType = (
  generated: CodeModel[],
  manuallyAdded: CodeModel[],
): CodeSourceTypes | undefined => {
  if (generated.length > 0) return CodeSourceTypes.Generated;
  if (manuallyAdded.length > 0) return CodeSourceTypes.ManuallyAdded;
  return undefined;
};

export const getProductOrServiceOfferDetails = (
  form: ProductOrServiceOfferDetailsForm,
  codeSourceType?: CodeSourceTypes,
): ProductOrServiceOfferDetails => {
  const allCodes = [...form.codes.generated, ...form.codes.manuallyAdded.valid];
  const defaultCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Default)
    .map(e => e.code);
  const pinCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Pin)
    .map(e => ({ code: e.code, pin: e.pin! }));

  return {
    defaultPrice: form.defaultPrice,
    discountedPrice: form.discountedPrice,
    defaultCodes,
    pinCodes,
    isCourse: form.isCourse,
    barcodeType: form.barcodeType?.id,
    codesExpirationDate: form.codesExpirationDate,
    codeSourceType:
      codeSourceType ??
      getCodeSourceType(form.codes.generated, form.codes.manuallyAdded.valid),
  };
};

export const getGiftCardOfferDetails = (
  form: GiftCardOfferDetailsForm,
  codeSourceType?: CodeSourceTypes,
): GiftCardOfferDetails => {
  const allCodes = [...form.codes.generated, ...form.codes.manuallyAdded.valid];
  const defaultCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Default)
    .map(e => e.code);
  const pinCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Pin)
    .map(e => ({ code: e.code, pin: e.pin! }));

  return {
    defaultPrice: form.defaultPrice,
    discountedPrice: form.discountedPrice,
    defaultCodes,
    pinCodes,
    orderInfo: form.orderInfo,
    emails: form.emails,
    barcodeType: form.barcodeType?.id,
    codesExpirationDate: form.codesExpirationDate,
    codeSourceType:
      codeSourceType ??
      getCodeSourceType(form.codes.generated, form.codes.manuallyAdded.valid),
  };
};

export const getValueVoucherOfferDetails = (
  form: ValueVoucherOfferDetailsForm,
): ValueVoucherOfferDetails => {
  return {
    customVoucherUrl: form.customVoucherUrl,
    customVoucherCode: form.customVoucherCode,
    customVoucherDiscount: form.customVoucherDiscount,
  };
};

export const getPercentageVoucherOfferDetails = (
  form: PercentageVoucherOfferDetailsForm,
): PercentageVoucherOfferDetails => {
  return {
    customVoucherDiscount: form.customVoucherDiscount,
    voucherDescription: form.voucherDescription,
    randomCode: form.randomCode!,
    barcode: form.barcode,
  };
};

export const getAddCodesCommand = (
  productId: number,
  form: ProductOrServiceOfferDetailsForm | GiftCardOfferDetailsForm,
  codeSourceType?: CodeSourceTypes,
): AddCodesCommand => {
  const allCodes = [...form.codes.generated, ...form.codes.manuallyAdded.valid];

  const defaultCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Default)
    .map(e => e.code);
  const pinCodes = allCodes
    .filter(e => e.type === SoCodeTypes.Pin)
    .map(e => ({ code: e.code, pin: e.pin! }));

  return {
    productId,
    defaultCodes,
    pinCodes,
    codesExpirationDate: form.codesExpirationDate,
    codeSourceType:
      codeSourceType ??
      getCodeSourceType(form.codes.generated, form.codes.manuallyAdded.valid),
  };
};

export const getRoName = (name: string) => `ro_${name}`;
export const getEnName = (name: string) => `en_${name}`;

export const getRoField = (value: any, name: string) => ({
  value,
  name: getRoName(name),
});

export const getEnField = (value: any, name: string) => ({
  value,
  name: getEnName(name),
});

export const getFullDescription = (
  language: LanguageTypes,
  detailsForm: ProductDetailsForm,
) => {
  const {
    brandDescription,
    description,
    address,
    phoneNumber,
    email,
    website,
  } =
    language === LanguageTypes.Ro
      ? detailsForm.productDescriptions.ro
      : detailsForm.productDescriptions.en;

  if (language === LanguageTypes.Ro) {
    return `<p>${brandDescription?.value ?? ''}</p>${
      description?.value ?? ''
    }<p>Adresa: ${address?.value ?? ''}</p><p>Număr de telefon: ${
      phoneNumber?.value ?? ''
    }</p><p>Email: ${email?.value ?? ''}</p><p>Website: ${
      website?.value ?? ''
    }</p>`;
  }

  if (language === LanguageTypes.En) {
    return `<p>${brandDescription?.value ?? ''}</p>${
      description?.value ?? ''
    }<p>Address: ${address?.value ?? ''}</p><p>Phone number: ${
      phoneNumber?.value ?? ''
    }</p><p>Email: ${email?.value ?? ''}</p><p>Website: ${
      website?.value ?? ''
    }</p>`;
  }
};

export const getAddProductCommand = (
  step: AddProductSteps,
  productType: ProductTypes,
  detailsForm: ProductDetailsForm,
  productOrServiceOfferDetailsForm: ProductOrServiceOfferDetailsForm,
  giftCardOfferDetailsForm: GiftCardOfferDetailsForm,
  valueVoucherOfferDetailsForm: ValueVoucherOfferDetailsForm,
  percentageVoucherOfferDetailsForm: PercentageVoucherOfferDetailsForm,
  productId?: number,
  sendForReview?: boolean,
): AddProductCommand => {
  if (step === AddProductSteps.ProductDetails) {
    return getAddProductCommandProductDetails(
      step,
      productType,
      detailsForm,
      productId,
      sendForReview,
    );
  }
  if (
    step === AddProductSteps.OfferDetails ||
    step === AddProductSteps.Translation
  ) {
    const initialData = getAddProductCommandProductDetails(
      step,
      productType,
      detailsForm,
      productId,
      sendForReview,
    );
    return {
      ...initialData,
      productOrServiceOfferDetails: getProductOrServiceOfferDetails(
        productOrServiceOfferDetailsForm,
      ),
      giftCardOfferDetails: getGiftCardOfferDetails(giftCardOfferDetailsForm),
      valueVoucherOfferDetails: getValueVoucherOfferDetails(
        valueVoucherOfferDetailsForm,
      ),
      percentageVoucherOfferDetails: getPercentageVoucherOfferDetails(
        percentageVoucherOfferDetailsForm,
      ),
    };
  }
  throw new Error();
};

const getAddProductCommandProductDetails = (
  step: AddProductSteps,
  productType: ProductTypes,
  detailsForm: ProductDetailsForm,
  productId?: number,
  sendForReview?: boolean,
): AddProductCommand => {
  return {
    productId,
    addProductStep: step,
    productType: productType,
    productActionType: sendForReview
      ? ProductActionTypes.SendForReview
      : ProductActionTypes.SaveAsDraft,
    productDetails: {
      image: detailsForm.image,
      filterIds: getLocationFilterIds(detailsForm),
      roDescription: {
        name: detailsForm.productDescriptions.ro.name,
        description: {
          value: getFullDescription(LanguageTypes.Ro, detailsForm),
          name: detailsForm.productDescriptions.ro.description.name,
        },
        howToOrder: detailsForm.productDescriptions.ro.howToOrder,
      },
      enDescription: {
        name: detailsForm.productDescriptions.en.name,
        description: {
          value: detailsForm.productDescriptions.en.description.value
            ? getFullDescription(LanguageTypes.En, detailsForm)
            : undefined,
          name: detailsForm.productDescriptions.en.description.name,
        },
        howToOrder: detailsForm.productDescriptions.en.howToOrder,
      },
    },
  };
};

export const getProductSentForReviewKey = (manufacturerId: number) =>
  `${localStorageKeys.productSentForReview}-${manufacturerId}`;

export const getProductSentForReviewData = (
  manufacturerId: number,
): { resourceKey: string; payload: any } | null => {
  const item = localStorage.getItem(getProductSentForReviewKey(manufacturerId));

  return item ? JSON.parse(item) : null;
};

export const setProductSentForReviewInfoMessage = (
  resourceKey: string,
  payload: any,
  manufacturerId: number,
) => {
  const data = { resourceKey, payload };
  localStorage.setItem(
    getProductSentForReviewKey(manufacturerId),
    JSON.stringify(data),
  );
};

export const clearProductSentForReviewInfoMessage = (
  manufacturerId: number,
) => {
  localStorage.removeItem(getProductSentForReviewKey(manufacturerId));
};

export const validate = (
  detailsForm: ProductDetailsForm,
  context: ProductContext,
  productType: ProductTypes,
  productOrServiceForm: ProductOrServiceOfferDetailsForm,
  giftCardForm: GiftCardOfferDetailsForm,
  percentageVoucherForm: PercentageVoucherOfferDetailsForm,
  valueVoucherForm: ValueVoucherOfferDetailsForm,
): { isValid: boolean; errors: ErrorState } => {
  const { isValid: productDetailsIsValid, errors: productDetailsErrors } =
    validateProductDetails(detailsForm, context);

  let resultIsValid = productDetailsIsValid;
  let resultErrors = { ...productDetailsErrors };

  if (productType === ProductTypes.ProductOrService) {
    const { isValid: productOrServiceIsValid, errors: productOrServiceErrors } =
      validateProductOrService(productOrServiceForm, context);
    resultIsValid = resultIsValid && productOrServiceIsValid;
    resultErrors = { ...resultErrors, ...productOrServiceErrors };
  }
  if (productType === ProductTypes.GiftCard) {
    const { isValid: giftCardIsValid, errors: giftCardErrors } =
      validateGiftCard(giftCardForm, context);
    resultIsValid = resultIsValid && giftCardIsValid;
    resultErrors = { ...resultErrors, ...giftCardErrors };
  }
  if (productType === ProductTypes.PercentageVoucher) {
    const {
      isValid: percentageVoucherIsValid,
      errors: percentageVoucherErrors,
    } = validatePercentageVoucher(percentageVoucherForm);
    resultIsValid = resultIsValid && percentageVoucherIsValid;
    resultErrors = { ...resultErrors, ...percentageVoucherErrors };
  }
  if (productType === ProductTypes.ValueVoucher) {
    const { isValid: valueVoucherIsValid, errors: valueVoucherErrors } =
      validateValueVoucher(valueVoucherForm);
    resultIsValid = resultIsValid && valueVoucherIsValid;
    resultErrors = { ...resultErrors, ...valueVoucherErrors };
  }

  return { isValid: resultIsValid, errors: resultErrors };
};

const validateName = (detailsForm: ProductDetailsForm, errors: ErrorState) => {
  return produce(errors, d => {
    if (!detailsForm.productDescriptions.ro.name.value) {
      d[getRoName(nameField)] = [
        getRequiredValidationError(getRoName(nameField)),
      ];
    }
    if (!detailsForm.productDescriptions.en.name.value) {
      d[getEnName(nameField)] = [
        getRequiredValidationError(getEnName(nameField)),
      ];
    }
  });
};

const validateProductDescriptions = (
  detailsForm: ProductDetailsForm,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (
      !detailsForm.productDescriptions.ro.description.value ||
      detailsForm.productDescriptions.ro.description.value ===
        descriptionInitialValue
    ) {
      d[getRoName(descriptionField)] = [
        getRequiredValidationError(getRoName(descriptionField)),
      ];
    }

    if (
      !detailsForm.productDescriptions.en.description.value ||
      detailsForm.productDescriptions.en.description.value ===
        descriptionInitialValue
    ) {
      d[getEnName(descriptionField)] = [
        getRequiredValidationError(getEnName(descriptionField)),
      ];
    }
  });
};

const validateBrandDescriptions = (
  detailsForm: ProductDetailsForm,
  checkContactInfo: boolean,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.ro.brandDescription?.value
    ) {
      d[getRoName(brandDescriptionField)] = [
        getRequiredValidationError(getRoName(brandDescriptionField)),
      ];
    }

    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.en.brandDescription?.value
    ) {
      d[getEnName(brandDescriptionField)] = [
        getRequiredValidationError(getEnName(brandDescriptionField)),
      ];
    }
  });
};

const validateEmail = (
  detailsForm: ProductDetailsForm,
  checkContactInfo: boolean,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (checkContactInfo && !detailsForm.productDescriptions.ro.email?.value) {
      d[getRoName(emailField)] = [
        getRequiredValidationError(getRoName(emailField)),
      ];
    }
    if (checkContactInfo && !detailsForm.productDescriptions.en.email?.value) {
      d[getEnName(emailField)] = [
        getRequiredValidationError(getEnName(emailField)),
      ];
    }
    if (
      checkContactInfo &&
      detailsForm.productDescriptions.ro.email?.value &&
      !emailRegex.test(detailsForm.productDescriptions.ro.email?.value)
    ) {
      d[getRoName(emailField)] = [
        {
          code: resources.validation.invalidEmail,
          target: getRoName(emailField),
          placeholders: {},
          severity: 'error',
        },
      ];
    }

    if (
      checkContactInfo &&
      detailsForm.productDescriptions.en.email?.value &&
      !emailRegex.test(detailsForm.productDescriptions.en.email?.value)
    ) {
      d[getEnName(emailField)] = [
        {
          code: resources.validation.invalidEmail,
          target: getEnName(emailField),
          placeholders: {},
          severity: 'error',
        },
      ];
    }
  });
};

const validateAddress = (
  detailsForm: ProductDetailsForm,
  checkContactInfo: boolean,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.ro.address?.value
    ) {
      d[getRoName(addressField)] = [
        getRequiredValidationError(getRoName(addressField)),
      ];
    }

    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.en.address?.value
    ) {
      d[getEnName(addressField)] = [
        getRequiredValidationError(getEnName(addressField)),
      ];
    }
  });
};

const validatePhoneNumber = (
  detailsForm: ProductDetailsForm,
  checkContactInfo: boolean,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.ro.phoneNumber?.value
    ) {
      d[getRoName(phoneNumberField)] = [
        getRequiredValidationError(getRoName(phoneNumberField)),
      ];
    }

    if (
      checkContactInfo &&
      detailsForm.productDescriptions.ro.phoneNumber?.value &&
      !onlyDigitsRegex.test(
        detailsForm.productDescriptions.ro.phoneNumber.value,
      )
    ) {
      d[getRoName(phoneNumberField)] = [
        {
          code: resources.validation.invalidPhoneNumber,
          target: getRoName(phoneNumberField),
          placeholders: {},
          severity: 'error',
        },
      ];
    }

    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.en.phoneNumber?.value
    ) {
      d[getEnName(phoneNumberField)] = [
        getRequiredValidationError(getEnName(phoneNumberField)),
      ];
    }
    if (
      checkContactInfo &&
      detailsForm.productDescriptions.en.phoneNumber?.value &&
      !onlyDigitsRegex.test(
        detailsForm.productDescriptions.en.phoneNumber.value,
      )
    ) {
      d[getEnName(phoneNumberField)] = [
        {
          code: resources.validation.invalidPhoneNumber,
          target: getEnName(phoneNumberField),
          placeholders: {},
          severity: 'error',
        },
      ];
    }
  });
};

const validateWebsite = (
  detailsForm: ProductDetailsForm,
  checkContactInfo: boolean,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.ro.website?.value
    ) {
      d[getRoName(websiteField)] = [
        getRequiredValidationError(getRoName(websiteField)),
      ];
    }
    if (
      checkContactInfo &&
      !detailsForm.productDescriptions.en.website?.value
    ) {
      d[getEnName(websiteField)] = [
        getRequiredValidationError(getEnName(websiteField)),
      ];
    }
  });
};

const validateHowToOrder = (
  detailsForm: ProductDetailsForm,
  errors: ErrorState,
) => {
  return produce(errors, d => {
    if (!detailsForm.productDescriptions.ro.howToOrder.value) {
      d[getRoName(howToOrderField)] = [
        getRequiredValidationError(getRoName(howToOrderField)),
      ];
    }

    if (!detailsForm.productDescriptions.en.howToOrder.value) {
      d[getEnName(howToOrderField)] = [
        getRequiredValidationError(getEnName(howToOrderField)),
      ];
    }
  });
};

const validateLocation = (
  detailsForm: ProductDetailsForm,
  errors: ErrorState,
) => {
  if (!detailsForm.locationType) {
    return produce(errors, d => {
      d.location = [getRequiredValidationError('location')];
    });
  }
};

export const validateProductDetails = (
  detailsForm: ProductDetailsForm,
  context: ProductContext,
): { isValid: boolean; errors: ErrorState } => {
  let errors: ErrorState = {};
  const checkContactInfo = context === ProductContext.Add;

  errors = validateName(detailsForm, errors) ?? errors;
  errors = validateProductDescriptions(detailsForm, errors) ?? errors;
  errors =
    validateBrandDescriptions(detailsForm, checkContactInfo, errors) ?? errors;
  errors = validateEmail(detailsForm, checkContactInfo, errors) ?? errors;
  errors = validateAddress(detailsForm, checkContactInfo, errors) ?? errors;
  errors = validatePhoneNumber(detailsForm, checkContactInfo, errors) ?? errors;
  errors = validateWebsite(detailsForm, checkContactInfo, errors) ?? errors;
  errors = validateHowToOrder(detailsForm, errors) ?? errors;
  errors = validateLocation(detailsForm, errors) ?? errors;

  return { isValid: Object.keys(errors).length == 0, errors };
};

export const validateProductOrService = (
  form: ProductOrServiceOfferDetailsForm,
  context: ProductContext,
): { isValid: boolean; errors: ErrorState } => {
  let isValid = true;
  let errors: ErrorState = {};

  if (_.isNil(form.defaultPrice)) {
    isValid = false;
    errors = produce(errors, d => {
      d.defaultPrice = [getRequiredValidationError('defaultPrice')];
    });
  }

  if (_.isNil(form.discountedPrice)) {
    isValid = false;
    errors = produce(errors, d => {
      d.discountedPrice = [getRequiredValidationError('discountedPrice')];
    });
  }

  if (!_.isNil(form.defaultPrice) && !_.isNil(form.discountedPrice)) {
    if (form.defaultPrice < form.discountedPrice) {
      isValid = false;
      errors = produce(errors, d => {
        d.defaultPrice = [getInvalidDiscountedPriceError('defaultPrice')];
        d.discountedPrice = [getInvalidDiscountedPriceError('discountedPrice')];
      });
    }
  }

  if (_.isNil(form.barcodeType)) {
    isValid = false;
    errors = produce(errors, d => {
      d.barcodeType = [getRequiredValidationError('barcodeType')];
    });
  }

  if (
    context === ProductContext.Add &&
    !form.codes.generated.length &&
    !form.codes.manuallyAdded.valid.length
  ) {
    isValid = false;
    errors = produce(errors, d => {
      d.codeSource = [getRequiredValidationError('codeSource')];
    });
  }

  return { isValid, errors };
};

export const validateGiftCard = (
  form: GiftCardOfferDetailsForm,
  context: ProductContext,
): { isValid: boolean; errors: ErrorState } => {
  let isValid = true;
  let errors: ErrorState = {};

  if (_.isNil(form.defaultPrice)) {
    isValid = false;
    errors = produce(errors, d => {
      d.defaultPrice = [getRequiredValidationError('defaultPrice')];
    });
  }

  if (_.isNil(form.discountedPrice)) {
    isValid = false;
    errors = produce(errors, d => {
      d.discountedPrice = [getRequiredValidationError('discountedPrice')];
    });
  }

  if (!_.isNil(form.defaultPrice) && !_.isNil(form.discountedPrice)) {
    if (form.defaultPrice < form.discountedPrice) {
      isValid = false;
      errors = produce(errors, d => {
        d.defaultPrice = [getInvalidDiscountedPriceError('defaultPrice')];
        d.discountedPrice = [getInvalidDiscountedPriceError('discountedPrice')];
      });
    }
  }

  if (_.isNil(form.barcodeType)) {
    isValid = false;
    errors = produce(errors, d => {
      d.barcodeType = [getRequiredValidationError('barcodeType')];
    });
  }

  if (
    context === ProductContext.Add &&
    !form.codes.generated.length &&
    !form.codes.manuallyAdded.valid.length
  ) {
    isValid = false;
    errors = produce(errors, d => {
      d.codeSource = [getRequiredValidationError('codeSource')];
    });
  }

  return { isValid, errors };
};

export const validatePercentageVoucher = (
  form: PercentageVoucherOfferDetailsForm,
): { isValid: boolean; errors: ErrorState } => {
  let isValid = true;
  let errors: ErrorState = {};

  if (_.isNil(form.customVoucherDiscount)) {
    isValid = false;
    errors = produce(errors, d => {
      d.customVoucherDiscount = [
        getRequiredValidationError('customVoucherDiscount'),
      ];
    });
  }

  if (_.isNil(form.voucherDescription)) {
    isValid = false;
    errors = produce(errors, d => {
      d.voucherDescription = [getRequiredValidationError('voucherDescription')];
    });
  }

  if (_.isNil(form.codeType)) {
    isValid = false;
    errors = produce(errors, d => {
      d.codeType = [getRequiredValidationError('codeType')];
    });
  }

  if (
    form.codeType === PercentageVoucherCodeTypes.ManuallyAdded &&
    !form.barcode
  ) {
    isValid = false;
    errors = produce(errors, d => {
      d.barcode = [getRequiredValidationError('barcode')];
    });
  }

  return { isValid, errors };
};

export const validateValueVoucher = (
  form: ValueVoucherOfferDetailsForm,
): { isValid: boolean; errors: ErrorState } => {
  let isValid = true;
  let errors: ErrorState = {};

  if (!form.customVoucherDiscount) {
    isValid = false;
    errors = produce(errors, d => {
      d.customVoucherDiscount = [
        getRequiredValidationError('customVoucherDiscount'),
      ];
    });
  }

  if (!form.customVoucherCode) {
    isValid = false;
    errors = produce(errors, d => {
      d.customVoucherCode = [getRequiredValidationError('customVoucherCode')];
    });
  }

  if (!form.customVoucherUrl) {
    isValid = false;
    errors = produce(errors, d => {
      d.customVoucherUrl = [getRequiredValidationError('customVoucherUrl')];
    });
  }

  return { isValid, errors };
};

export const getInvalidDiscountedPriceError = (
  target: string,
): ValidationError => {
  return {
    code: resources.products.add.validation.invalidDiscountedPrice,
    target,
    placeholders: {},
    severity: 'error',
  };
};

export const setEditorFontOnMount = (e: EditorMountEvent) => {
  const iframeDocument = e.dom.ownerDocument;
  const style = iframeDocument.createElement('style');
  style.appendChild(
    iframeDocument.createTextNode(
      'p { font-size: 15px; font-family: Edenred, Ubuntu, sans-serif; }',
    ),
  );
  iframeDocument.head.appendChild(style);
};

export interface ProductDescription {
  name: NamedField<string | undefined>;
  description: NamedField<string | undefined>;
  brandDescription?: NamedField<string | undefined>;
  address?: NamedField<string | undefined>;
  email?: NamedField<string | undefined>;
  phoneNumber?: NamedField<string | undefined>;
  website?: NamedField<string | undefined>;
  howToOrder: NamedField<string | undefined>;
}

export interface ProductDetailsForm {
  image?: string;
  imageContent?: string;
  productDescriptions: {
    ro: ProductDescription;
    en: ProductDescription;
  };
  files?: UploadFileInfo[];
  locationType?: ProductLocationTypes;
  selectedCountiesWithCities?: CountyFilterModel[];
}
